import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, Zap, X, Check, Crown } from "lucide-react";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";

/* ─── Upgrade Prompt (full-page gate for Pro features) ─── */
export function UpgradePrompt({ feature, onUpgraded }) {
  const { user, checkAuth } = useAuth();
  const [starting, setStarting] = useState(false);
  const sub = user?.subscription;
  const hasUsedTrial = sub?.trial_start_date;
  const isExpired = sub?.subscription_status === "expired";

  const handleStartTrial = async () => {
    setStarting(true);
    try {
      await apiClient.post("/subscription/start-trial");
      toast.success("Pro trial started! You have 7 days of full access.");
      await checkAuth();
      if (onUpgraded) onUpgraded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start trial");
    } finally {
      setStarting(false);
    }
  };

  const handleUpgrade = async () => {
    setStarting(true);
    try {
      const res = await apiClient.post("/subscription/checkout", {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setStarting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[50vh]" data-testid="upgrade-prompt">
      <Card className="bg-card border-border max-w-md w-full">
        <CardContent className="p-8 text-center space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            {isExpired ? <Lock className="h-7 w-7 text-primary" /> : <Sparkles className="h-7 w-7 text-primary" />}
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold mb-1">
              {isExpired ? "Your trial has ended" : hasUsedTrial ? "Upgrade to Fretless Pro" : "Start your 7-day free trial"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isExpired
                ? "Upgrade to continue using Pro features."
                : hasUsedTrial
                  ? `${feature} requires a Pro plan.`
                  : "Try all Pro features free for 7 days. Cancel anytime."}
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-secondary/30 rounded-lg p-4 text-left space-y-2">
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-primary" /> Unlimited gigs</div>
              <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-primary" /> Export gigs to Bandsintown/Songkick</div>
              <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-primary" /> Generate social media posts instantly</div>
            </div>
          </div>

          {/* CTA */}
          {hasUsedTrial || isExpired ? (
            <div className="space-y-2">
              <Button
                onClick={handleUpgrade}
                disabled={starting}
                className="w-full bg-primary text-primary-foreground"
                data-testid="upgrade-pro-btn"
              >
                {starting ? "Redirecting..." : "Upgrade to Fretless Pro — £5.99/mo"}
              </Button>
              <p className="text-xs text-muted-foreground">One gig pays for this app for the whole year.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                onClick={handleStartTrial}
                disabled={starting}
                className="w-full bg-primary text-primary-foreground"
                data-testid="start-trial-btn"
              >
                {starting ? "Starting..." : "Start Free Trial"}
              </Button>
              <p className="text-xs text-muted-foreground">No payment required to start</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Trial Banner (shown on Dashboard) ─── */
export function TrialBanner({ subscription, onClose }) {
  if (!subscription) return null;

  const { plan_type, subscription_status, trial_end_date, is_pro, gig_count_this_month, gig_limit } = subscription;

  // Trial countdown
  if (plan_type === "trial" && subscription_status === "trialing" && trial_end_date) {
    const daysLeft = Math.max(0, Math.ceil((new Date(trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)));
    return (
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2.5 flex items-center justify-between mb-4" data-testid="trial-banner">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <span>Your free trial ends in <strong>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong></span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Expired trial
  if (subscription_status === "expired") {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5 flex items-center justify-between mb-4" data-testid="trial-expired-banner">
        <div className="flex items-center gap-2 text-sm">
          <Lock className="h-4 w-4 text-red-500" />
          <span>Your Pro trial has expired. Some features are now limited.</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Gig limit warning for free users
  if (!is_pro && gig_limit && gig_count_this_month >= gig_limit - 3) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2.5 flex items-center justify-between mb-4" data-testid="gig-limit-banner">
        <div className="flex items-center gap-2 text-sm">
          <Zap className="h-4 w-4 text-amber-500" />
          <span>{gig_count_this_month}/{gig_limit} gigs this month</span>
        </div>
      </div>
    );
  }

  return null;
}

/* ─── Plan Badge (shown in nav header) ─── */
export function PlanBadge({ subscription }) {
  if (!subscription) return null;
  const { plan_type, subscription_status } = subscription;

  if (plan_type === "pro" && subscription_status === "active") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary" data-testid="plan-badge-pro">
        <Crown className="h-3 w-3" /> Pro
      </span>
    );
  }
  if (plan_type === "trial" && subscription_status === "trialing") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500" data-testid="plan-badge-trial">
        <Sparkles className="h-3 w-3" /> Trial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="plan-badge-free">
      Free
    </span>
  );
}

/* ─── Upgrade Modal (triggered by trial expiry) ─── */
export function UpgradeModal({ show, onClose, type }) {
  const [loading, setLoading] = useState(false);

  if (!show) return null;

  const isExpired = type === "expired";

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post("/subscription/checkout", {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Failed to start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="upgrade-modal">
      <Card className="bg-card border-border max-w-sm w-full mx-4">
        <CardContent className="p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            {isExpired ? <Lock className="h-6 w-6 text-primary" /> : <Sparkles className="h-6 w-6 text-amber-500" />}
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold mb-1">
              {isExpired ? "Your trial has ended" : "Your trial is ending soon"}
            </h3>
            <p className="text-muted-foreground text-sm">
              {isExpired
                ? "Upgrade to continue using Pro features."
                : "Keep unlimited gigs and pro features by upgrading now."}
            </p>
          </div>
          <div className="space-y-2">
            <Button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full bg-primary text-primary-foreground"
              data-testid="modal-upgrade-btn"
            >
              {loading ? "Redirecting..." : "Upgrade to Fretless Pro — £5.99/mo"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="w-full text-muted-foreground"
              data-testid="modal-dismiss-btn"
            >
              Maybe later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Pricing Card (for Settings/Pricing page) ─── */
export function PricingCard({ subscription }) {
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const isPro = subscription?.is_pro;
  const isLifetime = subscription?.lifetime_access;
  const isTrialing = subscription?.plan_type === "trial" && subscription?.subscription_status === "trialing";
  const isCancelling = subscription?.subscription_status === "cancelling";
  const isExpired = subscription?.subscription_status === "expired";
  const hasUsedTrial = subscription?.trial_start_date;
  const cancelAt = subscription?.cancel_at;

  const daysLeft = isTrialing && subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      await apiClient.post("/subscription/start-trial");
      toast.success("Pro trial started! 7 days of full access.");
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start trial");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post("/subscription/checkout", {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Failed to start checkout");
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your Pro subscription? You'll keep access until the end of your billing period.")) return;
    setLoading(true);
    try {
      const res = await apiClient.post("/subscription/cancel");
      toast.success("Subscription cancelled. Access continues until " + new Date(res.data.cancel_at).toLocaleDateString());
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="pricing-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Pro Plan</h3>
            <p className="text-muted-foreground text-sm">Everything you need to run your gigs</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold">£5.99</span>
            <span className="text-muted-foreground text-sm">/mo</span>
          </div>
        </div>

        {/* Status */}
        {isPro && isLifetime && (
          <div className="bg-primary/10 rounded-lg px-3 py-2 text-sm text-primary flex items-center gap-2" data-testid="plan-active-badge">
            <Crown className="h-4 w-4" /> Lifetime Pro access
          </div>
        )}
        {isPro && !isTrialing && !isLifetime && !isCancelling && (
          <div className="bg-primary/10 rounded-lg px-3 py-2 text-sm text-primary flex items-center gap-2" data-testid="plan-active-badge">
            <Crown className="h-4 w-4" /> Active Pro subscription
            {subscription?.next_billing_date && (
              <span className="text-primary/70 ml-auto text-xs">
                Next billing: {new Date(subscription.next_billing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            )}
          </div>
        )}
        {isCancelling && cancelAt && (
          <div className="bg-amber-500/10 rounded-lg px-3 py-2 text-sm text-amber-500" data-testid="plan-cancelling-badge">
            Your Pro access continues until {new Date(cancelAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. After that you'll move to the free plan.
          </div>
        )}
        {isTrialing && (
          <div className="bg-amber-500/10 rounded-lg px-3 py-2 text-sm text-amber-500 flex items-center gap-2" data-testid="plan-trial-badge">
            <Sparkles className="h-4 w-4" /> Trial active — {daysLeft} day{daysLeft !== 1 ? "s" : ""} left
          </div>
        )}

        {/* Benefits */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" /> Unlimited gigs</div>
          <div className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" /> Export gigs to Bandsintown/Songkick</div>
          <div className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" /> Generate social media posts instantly</div>
        </div>

        {/* CTA */}
        {isPro && !isTrialing && !isCancelling && !isLifetime ? (
          <Button
            onClick={handleCancel}
            disabled={loading}
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            data-testid="cancel-subscription-btn"
          >
            {loading ? "Cancelling..." : "Cancel subscription"}
          </Button>
        ) : isTrialing ? (
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            variant="outline"
            className="w-full"
            data-testid="pricing-upgrade-btn"
          >
            {loading ? "Redirecting..." : "Upgrade Now — Keep Pro Access"}
          </Button>
        ) : (!isPro || isCancelling) && (hasUsedTrial || isExpired || isCancelling) ? (
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full bg-primary text-primary-foreground"
            data-testid="pricing-upgrade-btn"
          >
            {loading ? "Redirecting..." : "Upgrade to Fretless Pro — £5.99/mo"}
          </Button>
        ) : !isPro ? (
          <div className="space-y-2">
            <Button
              onClick={handleStartTrial}
              disabled={loading}
              className="w-full bg-primary text-primary-foreground"
              data-testid="pricing-trial-btn"
            >
              {loading ? "Starting..." : "Start Free 7-Day Trial"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">No payment required to start</p>
          </div>
        ) : null}

        <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
          One gig pays for this app for the whole year.
        </p>
      </CardContent>
    </Card>
  );
}
